import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Link, Typography } from '@mui/material';

import type { OpportunityRelatedInclusion } from '~/hooks/apis/offers/useGetOpportunityRelated';

import AssetFieldGrid from './AssetFieldGrid';
import { formatCreatedOrModifiedBy, formatPicklistValues, formatSyncedMoney, salesforceRecordUrl } from './utils';

interface Props {
  inclusion: OpportunityRelatedInclusion;
  onOpenAsset?: () => void;
}

export default function InclusionAssetDetailPanel({ inclusion, onOpenAsset }: Props) {
  const canOpenAsset = !!onOpenAsset && !!inclusion.asset;
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, px: 2 }}>
        {inclusion.name}
      </Typography>

      <Box sx={{ mb: 3, px: 2 }}>
        <AssetFieldGrid
          left={[
            {
              label: 'Asset',
              value: canOpenAsset ? (
                <Link component="button" type="button" variant="body2" onClick={onOpenAsset}>
                  {inclusion.asset__name}
                </Link>
              ) : (
                inclusion.asset__name
              ),
            },
            { label: 'Inclusion Name', value: inclusion.name },
            {
              label: 'Deal Offer',
              value: inclusion.deal_option_name__c || inclusion.deal_offer__c,
              href: salesforceRecordUrl('Deal_Offer__c', inclusion.deal_offer__c),
            },
            { label: 'RRP', value: formatSyncedMoney(inclusion.currencyisocode, inclusion.rrp__c) },
            { label: 'Inclusion Description', value: inclusion.inclusion_description__c },
            { label: 'Type', value: inclusion.type__c },
            {
              label: 'Key',
              value: inclusion.key__c ? (
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {inclusion.key__c}
                </Typography>
              ) : undefined,
            },
            { label: 'Applies', value: formatPicklistValues(inclusion.applies__c) },
            { label: 'Valid For', value: formatPicklistValues(inclusion.valid_for__c) },
            { label: 'Venues', value: inclusion.venues__c },
            {
              label: 'Inclusions Total Value',
              value: formatSyncedMoney(inclusion.currencyisocode, inclusion.inclusions_total_value__c),
            },
          ]}
          right={[
            { label: 'Currency', value: inclusion.currencyisocode },
            { label: 'Total Value', value: formatSyncedMoney(inclusion.currencyisocode, inclusion.total_value__c) },
          ]}
        />
      </Box>

      <Accordion defaultExpanded disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Dynamic Products Specific</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AssetFieldGrid
            left={[
              { label: 'Included For Extra Guest', value: inclusion.included_for_extra_guest__c },
              { label: 'Rate Type', value: formatPicklistValues(inclusion.rate_type__c) },
            ]}
            right={[
              { label: 'Applicable Length of Stay', value: inclusion.applicable_length_of_stay__c },
              { label: 'Applicable to', value: formatPicklistValues(inclusion.applicable_to__c) },
            ]}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Bundle &amp; Save Specific</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AssetFieldGrid
            left={[{ label: 'Applicable to which Partner?', value: inclusion.applicable_to_which_partner__c }]}
            right={[]}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">System Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AssetFieldGrid
            left={[
              {
                label: 'Created By',
                value: formatCreatedOrModifiedBy(inclusion.createdby__name, inclusion.createddate),
              },
            ]}
            right={[
              {
                label: 'Last Modified By',
                value: formatCreatedOrModifiedBy(inclusion.lastmodifiedby__name, inclusion.lastmodifieddate),
              },
            ]}
          />
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 2, px: 2 }}>
        <Button
          component="a"
          href={salesforceRecordUrl('Inclusion__c', inclusion.id)}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<OpenInNewIcon />}
          variant="text"
          size="small"
        >
          Open in Salesforce
        </Button>
      </Box>
    </Box>
  );
}
